import React from 'react';
import Select from 'react-select';
import '../assets/FleetAnalytics.css';
import { LoadingSkeleton } from '../reports/LoadingSkeleton';
import deviceDataService from '../../services/deviceDataService';
import usePagination from '../utils/usePagination';
import { selectStylesMulti } from '../utils/selectStyles';

const ALL = 'ALL';
const GRID_ROWS_PER_PAGE = 5;
const EXPANDED_ROWS_PER_PAGE = 10;

const METRIC_LABELS = {
    usrPercent: 'User processes',
    nicePercent: 'Low-priority user processes',
    sysPercent: 'System processes',
    ioWaitPercent: 'Waiting for I/O',
    idlePercent: 'Idle CPU',
    stealPercent: 'CPU time used by virtualization',
    guestPercent: 'Virtual machine CPU time',
    guestNicePercent: 'Low-priority virtual machine CPU time',
    softPercent: 'Software interrupt handling',
    irqPercent: 'Hardware interrupt handling'
};

const METRIC_DESCRIPTIONS = {
    nicePercent: 'Time spent running lower-priority user processes.',
    stealPercent: 'Time taken from this CPU by the host virtualization layer.',
    guestPercent: 'Time spent running virtual machine processes.',
    guestNicePercent: 'Time spent running lower-priority virtual machine processes.'
};

const FALLBACK_METRICS = Object.keys(METRIC_LABELS);

function metricLabel(metric) {
    return METRIC_LABELS[metric] || metric;
}

function cpuIdSort(a, b) {
    const aNumber = Number(a);
    const bNumber = Number(b);
    if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) return aNumber - bNumber;
    return String(a).localeCompare(String(b));
}

function formatValue(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '—';
}

function compareRows(a, b, column) {
    if (column === 'systemId') {
        return String(a.systemId || '').localeCompare(String(b.systemId || ''));
    }

    const aValue = a[column];
    const bValue = b[column];
    const aMissing = aValue === null || aValue === undefined || Number.isNaN(Number(aValue));
    const bMissing = bValue === null || bValue === undefined || Number.isNaN(Number(bValue));
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    return Number(aValue) - Number(bValue);
}

const CPU_HEALTH_SELECT_STYLES = {
    ...selectStylesMulti,
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        height: 'auto',
        borderColor: state.isFocused ? '#3253DC' : '#cbd5e1',
        boxShadow: state.isFocused ? '0 0 0 1px #3253DC' : 'none'
    }),
    valueContainer: (base) => ({
        ...base,
        minHeight: '36px',
        height: 'auto',
        maxHeight: '70px',
        overflowY: 'auto',
        flexWrap: 'wrap'
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '4px'
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#1e40af',
        fontSize: '11px'
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#3253DC',
        ':hover': { backgroundColor: '#dbeafe', color: '#1e3a8a' }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 1200 })
};

function sortIndicator(column, activeColumn, direction) {
    if (column !== activeColumn) return '↕';
    return direction === 'asc' ? '↑' : '↓';
}

function SortableHeader({ column, label, activeColumn, direction, onSort }) {
    return (
        <th scope="col">
            <button type="button" className="cpu-health-sort-button" onClick={() => onSort(column)}>
                <span>{label}</span>
                <span aria-hidden="true">{sortIndicator(column, activeColumn, direction)}</span>
            </button>
        </th>
    );
}

function ExpandIcon() {
    return (
        <svg className="cpu-health-expand-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 3H3v5M3 3l6 6M16 3h5v5M21 3l-6 6M8 21H3v-5M3 21l6-6M16 21h5v-5M21 21l-6-6" />
        </svg>
    );
}

function CpuStatisticsTable({
    cpuId,
    metric,
    rows,
    onDeviceClick,
    refreshing,
    rowsPerPage,
    onExpand,
    expanded = false
}) {
    const [sortColumn, setSortColumn] = React.useState('systemId');
    const [sortDirection, setSortDirection] = React.useState('asc');
    const [currentPage, setCurrentPage] = React.useState(1);

    const sortedRows = React.useMemo(() => {
        return [...rows].sort((a, b) => {
            const result = compareRows(a, b, sortColumn);
            return sortDirection === 'asc' ? result : -result;
        });
    }, [rows, sortColumn, sortDirection]);

    React.useEffect(() => {
        setCurrentPage(1);
    }, [rows, sortColumn, sortDirection, rowsPerPage]);

    const { pageItems, totalPages, renderPageNumbers } = usePagination(
        sortedRows,
        currentPage,
        setCurrentPage,
        rowsPerPage
    );

    const handleSort = (column) => {
        if (column === sortColumn) {
            setSortDirection((direction) => direction === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const title = cpuId === 'all' ? 'Aggregate CPU' : `CPU ${cpuId}`;

    return (
        <div className={`cpu-health-table-shell${expanded ? ' cpu-health-table-shell-expanded' : ''}`}>
            <div className="cpu-health-table-heading">
                <div className="cpu-health-table-title">
                    <strong>{title}</strong>
                    <span title={METRIC_DESCRIPTIONS[metric] || undefined}>{metricLabel(metric)}</span>
                </div>
                <div className="cpu-health-table-actions">
                    {refreshing && (
                        <span className="cpu-health-refreshing" role="status">
                            <span className="cpu-health-spinner" aria-hidden="true" />
                            Updating…
                        </span>
                    )}
                    <span className="cpu-health-unit">%</span>
                    {!expanded && (
                        <button type="button" className="cpu-health-expand-button" onClick={onExpand} aria-label="Expand table" title="Expand table"><ExpandIcon /></button>
                    )}
                </div>
            </div>
            <div className="cpu-health-table-scroll">
                <table className="cpu-health-table">
                    <thead>
                        <tr>
                            <SortableHeader column="systemId" label="System ID" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="sampleCount" label="Samples" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="min" label="Min" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="max" label="Max" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="average" label="Avg" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="standardDeviation" label="Std Dev" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.length === 0 ? (
                            <tr><td colSpan="6" className="cpu-health-no-rows">No device rows available.</td></tr>
                        ) : pageItems.map((row) => (
                            <tr key={`${row.systemId}-${row.cpuId}-${row.metric}`}>
                                <td>
                                    <button
                                        type="button"
                                        className="cpu-health-device-link"
                                        title="Open device overview"
                                        onClick={() => onDeviceClick && onDeviceClick(row.systemId)}
                                    >
                                        {row.systemId}
                                    </button>
                                </td>
                                <td>{row.sampleCount ?? 0}</td>
                                <td>{formatValue(row.min)}</td>
                                <td>{formatValue(row.max)}</td>
                                <td>{formatValue(row.average)}</td>
                                <td>{formatValue(row.standardDeviation)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="cpu-health-pagination">
                <span>{sortedRows.length} device{sortedRows.length === 1 ? '' : 's'} · {totalPages > 1 ? `Page ${currentPage} of ${totalPages}` : 'Page 1'}</span>
                <div className="cpu-health-page-buttons">{renderPageNumbers()}</div>
            </div>
        </div>
    );
}

function selectedOptions(options, values) {
    return options.filter((option) => values.includes(option.value));
}

function applyMultiSelectChange(options, actionMeta) {
    const values = (options || []).map((option) => option.value);
    if (actionMeta.action === 'clear' || values.length === 0) return [ALL];
    if (actionMeta.action === 'select-option' && actionMeta.option?.value === ALL) return [ALL];
    const withoutAll = values.filter((value) => value !== ALL);
    return withoutAll.length ? withoutAll : [ALL];
}

function selectionSummary(values, label) {
    return values.includes(ALL) ? `All ${label}` : `${values.length} ${label} selected`;
}

function CpuHealthSection({ timeRange, deviceTag, onDeviceClick }) {
    const isDeviceTagView = deviceTag !== undefined;
    const [selectedCpuId, setSelectedCpuId] = React.useState('0');
    const [selectedMetrics, setSelectedMetrics] = React.useState([ALL]);
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [reloadToken, setReloadToken] = React.useState(0);
    const [expandedGroup, setExpandedGroup] = React.useState(null);

    React.useEffect(() => {
        if (isDeviceTagView && !deviceTag) {
            setData(null);
            setError(null);
            setLoading(false);
            return undefined;
        }

        let active = true;
        setLoading(true);
        setError(null);

        deviceDataService.getCpuHealth(timeRange, deviceTag ?? null, selectedCpuId, selectedMetrics)
            .then((result) => {
                if (active) setData(result);
            })
            .catch((requestError) => {
                if (active) setError(requestError);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [timeRange, deviceTag, isDeviceTagView, selectedCpuId, selectedMetrics, reloadToken]);

    React.useEffect(() => {
        if (!expandedGroup) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setExpandedGroup(null);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [expandedGroup]);

    const cpuIds = React.useMemo(() => (data?.cpuIds || [])
        .filter((cpuId) => cpuId !== 'all')
        .slice()
        .sort(cpuIdSort), [data?.cpuIds]);
    const metrics = data?.metrics?.length ? data.metrics : FALLBACK_METRICS;
    const cpuOptions = cpuIds.map((cpuId) => ({ value: cpuId, label: `CPU ${cpuId}` }));
    const metricOptions = [{ value: ALL, label: 'All' }, ...metrics.map((metric) => ({ value: metric, label: metricLabel(metric) }))];

    const groupedRows = React.useMemo(() => {
        const groups = new Map();
        (data?.rows || []).forEach((row) => {
            const key = `${row.cpuId}::${row.metric}`;
            if (!groups.has(key)) groups.set(key, { cpuId: row.cpuId, metric: row.metric, rows: [] });
            groups.get(key).rows.push(row);
        });
        return [...groups.values()].sort((a, b) => {
            const cpuOrder = cpuIdSort(a.cpuId, b.cpuId);
            return cpuOrder || a.metric.localeCompare(b.metric);
        });
    }, [data]);

    React.useEffect(() => {
        if (cpuIds.length > 0 && !cpuIds.includes(selectedCpuId)) {
            setSelectedCpuId(cpuIds.includes('0') ? '0' : cpuIds[0]);
        }
    }, [cpuIds, selectedCpuId]);

    const handleCpuChange = React.useCallback((option) => {
        if (option?.value) setSelectedCpuId(option.value);
    }, []);

    const handleMetricChange = React.useCallback((options, actionMeta) => {
        setSelectedMetrics(applyMultiSelectChange(options, actionMeta));
    }, []);

    return (
        <div className="cpu-health-section">
            <div className="cpu-health-toolbar">
                <div className="cpu-health-filters">
                    <label>
                        <span>CPU ID <em>CPU {selectedCpuId}</em></span>
                        <Select
                            className="cpu-health-select"
                            classNamePrefix="cpu-health-select"
                            isSearchable
                            isClearable={false}
                            isLoading={loading}
                            menuPortalTarget={document.body}
                            options={cpuOptions}
                            value={cpuOptions.find((option) => option.value === selectedCpuId) || { value: selectedCpuId, label: `CPU ${selectedCpuId}` }}
                            onChange={handleCpuChange}
                            styles={CPU_HEALTH_SELECT_STYLES}
                            placeholder="Select CPU ID…"
                            aria-label="Select CPU ID"
                        />
                    </label>
                    <label>
                        <span>CPU Metric <em>{selectionSummary(selectedMetrics, 'metrics')}</em></span>
                        <Select
                            className="cpu-health-select"
                            classNamePrefix="cpu-health-select"
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isSearchable
                            isLoading={loading}
                            menuPortalTarget={document.body}
                            options={metricOptions}
                            value={selectedOptions(metricOptions, selectedMetrics)}
                            onChange={handleMetricChange}
                            styles={CPU_HEALTH_SELECT_STYLES}
                            placeholder="Select metrics…"
                            aria-label="Select CPU metrics"
                        />
                    </label>
                </div>
            </div>

            {isDeviceTagView && !deviceTag ? (
                <div className="cpu-health-state">Select a device group to load CPU Health.</div>
            ) : loading ? (
                <div className="cpu-health-grid">
                    <LoadingSkeleton height="220px" />
                    <LoadingSkeleton height="220px" />
                </div>
            ) : error ? (
                <div className="cpu-health-state cpu-health-error">
                    <strong>CPU Health unavailable.</strong>
                    <span>{error.message || 'Unable to load CPU statistics.'}</span>
                    <button type="button" onClick={() => setReloadToken((current) => current + 1)}>Retry</button>
                </div>
            ) : groupedRows.length === 0 ? (
                <div className="cpu-health-state">No CPU statistics are available for this scope and time range.</div>
            ) : (
                <>
                    <div className="cpu-health-summary">
                        <span>{data.coreCount} logical core{data.coreCount === 1 ? '' : 's'} observed</span>
                        <span>{groupedRows.length} CPU ID / metric table{groupedRows.length === 1 ? '' : 's'}</span>
                    </div>
                    <div className="cpu-health-grid">
                        {groupedRows.map((group) => (
                            <CpuStatisticsTable
                                key={`${group.cpuId}-${group.metric}`}
                                cpuId={group.cpuId}
                                metric={group.metric}
                                rows={group.rows}
                                rowsPerPage={GRID_ROWS_PER_PAGE}
                                refreshing={loading}
                                onDeviceClick={onDeviceClick}
                                onExpand={() => setExpandedGroup(group)}
                            />
                        ))}
                    </div>
                </>
            )}

            {expandedGroup && (
                <div className="cpu-health-modal-overlay" role="presentation" onMouseDown={(event) => {
                    if (event.target === event.currentTarget) setExpandedGroup(null);
                }}>
                    <div className="cpu-health-modal" role="dialog" aria-modal="true" aria-label={`${metricLabel(expandedGroup.metric)} table`}>
                        <div className="cpu-health-modal-header">
                            <div>
                                <strong>CPU Health details</strong>
                                <span>Expanded device view · up to 10 rows per page</span>
                            </div>
                            <button type="button" onClick={() => setExpandedGroup(null)} aria-label="Close expanded table">×</button>
                        </div>
                        <div className="cpu-health-modal-body">
                            <CpuStatisticsTable
                                cpuId={expandedGroup.cpuId}
                                metric={expandedGroup.metric}
                                rows={expandedGroup.rows}
                                rowsPerPage={EXPANDED_ROWS_PER_PAGE}
                                refreshing={loading}
                                onDeviceClick={onDeviceClick}
                                expanded
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CpuHealthSection;
